import styled from 'styled-components';
import { motion } from 'framer-motion';
import { SquareArrowOutUpRight, X, PanelLeftOpen, PanelLeftClose } from 'lucide-react';

const PanelContainer = styled(motion.div)`
    flex: 1;
    min-width: 0;
    display: flex;
    flex-direction: column;
    height: 100vh;
    border-left: 1px solid #e0e0e0;
    background-color: #fff;
`;

const PanelHeader = styled.div`
    display: flex;
    align-items: center;
    gap: 6px;
    padding: 6px 10px;
    border-bottom: 1px solid #e0e0e0;
    background-color: #f8f9fa;
    flex-shrink: 0;
`;

const TabBar = styled.div`
    display: flex;
    align-items: center;
    gap: 4px;
    flex: 1;
    min-width: 0;
    overflow-x: auto;
    scrollbar-width: thin;

    &::-webkit-scrollbar {
        height: 4px;
    }
    &::-webkit-scrollbar-thumb {
        background: #d5d9df;
        border-radius: 4px;
    }
`;

const Tab = styled.button`
    display: flex;
    align-items: center;
    gap: 6px;
    height: 32px;
    padding: 0 8px 0 12px;
    border: 1px solid transparent;
    border-radius: 8px;
    background: transparent;
    color: #6b7280;
    font-size: 13px;
    font-weight: 600;
    white-space: nowrap;
    cursor: pointer;
    transition: all 0.15s ease;

    &:hover {
        background: #eef0f3;
        color: #374151;
    }

    &.active {
        background: #ffffff;
        color: var(--primary);
        border-color: #e0e4e9;
        box-shadow: var(--shadow-xs);
    }

    .tab-name {
        overflow: hidden;
        text-overflow: ellipsis;
        max-width: 160px;
    }

    .tab-close {
        display: flex;
        align-items: center;
        justify-content: center;
        width: 18px;
        height: 18px;
        border-radius: 4px;
        color: #9ca3af;
        transition: background 0.15s ease, color 0.15s ease;

        &:hover {
            background: #e5e7eb;
            color: #374151;
        }
    }
`;

const IconButton = styled.button`
    display: flex;
    align-items: center;
    justify-content: center;
    width: 30px;
    height: 30px;
    padding: 0;
    border: none;
    border-radius: 6px;
    background-color: transparent;
    color: #555;
    cursor: pointer;
    flex-shrink: 0;
    transition: background-color 0.15s ease;

    &:hover {
        background-color: #e9ecef;
    }
`;

const PanelBody = styled.div`
    flex: 1;
    min-height: 0;
    position: relative;
`;

const StyledIframe = styled.iframe`
    position: absolute;
    inset: 0;
    width: 100%;
    height: 100%;
    border: none;
    display: block;
`;

/*
    반품쟁이 화면 우측에 붙는 iframe 탭 패널.
    - services: 열려 있는 서비스 목록 [{ key, name, url }]
    - activeKey: 현재 활성 탭 key
    - 모든 iframe 을 동시에 마운트해두고 visibility 만 토글 → 탭 전환 시 리로드 없이 상태 유지.
    - onSelectTab(key): 탭 전환
    - onCloseTab(key): 개별 탭 닫기 (마지막 탭을 닫으면 패널이 닫힘)
    - sidebarVisible / onToggleSidebar: 좌측 사이드바 토글
*/
export function WegetIframePanel({ services, activeKey, sidebarVisible, onSelectTab, onCloseTab, onToggleSidebar }) {
    if (!services || services.length === 0) {
        return null;
    }

    const activeService = services.find(service => service.key === activeKey) || services[0];

    return (
        <PanelContainer
            initial={{ x: 40, opacity: 0 }}
            animate={{ x: 0, opacity: 1 }}
            transition={{ duration: 0.2, ease: 'easeOut' }}
        >
            <PanelHeader>
                <IconButton
                    type='button'
                    title={sidebarVisible ? '사이드바 닫기' : '사이드바 열기'}
                    onClick={() => onToggleSidebar?.()}
                >
                    {sidebarVisible ? <PanelLeftClose size={16} /> : <PanelLeftOpen size={16} />}
                </IconButton>
                <TabBar>
                    {services.map(service => (
                        <Tab
                            key={service.key}
                            type='button'
                            className={service.key === activeService.key ? 'active' : ''}
                            onClick={() => onSelectTab?.(service.key)}
                            title={service.name}
                        >
                            <span className='tab-name'>{service.name}</span>
                            <span
                                className='tab-close'
                                role='button'
                                title='탭 닫기'
                                onClick={(e) => {
                                    e.stopPropagation();
                                    onCloseTab?.(service.key);
                                }}
                            >
                                <X size={13} />
                            </span>
                        </Tab>
                    ))}
                </TabBar>
                <IconButton
                    type='button'
                    title='새 탭으로 열기'
                    onClick={() => window.open(activeService.url, '_blank', 'noopener,noreferrer')}
                >
                    <SquareArrowOutUpRight size={16} />
                </IconButton>
            </PanelHeader>
            <PanelBody>
                {services.map(service => (
                    <StyledIframe
                        key={service.key}
                        src={service.url}
                        title={service.name}
                        style={{ visibility: service.key === activeService.key ? 'visible' : 'hidden' }}
                    />
                ))}
            </PanelBody>
        </PanelContainer>
    );
}

export default WegetIframePanel;
